import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { buildStaffItems } from "@/components/Sidebar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LayoutDashboard, MoreHorizontal, LogOut, KeyRound, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

/**
 * Bottom navigation for `special_access` users — they primarily work on
 * mobile so we drop the side bar entirely and surface their 4 most-used
 * destinations as thumb-reachable tabs with a "More" sheet for everything
 * else.
 *
 * Flattens the regular staff sidebar tree (which may contain `children`
 * groups like Students/Employees/Accounting) into a flat list of leaf links
 * so each tab targets a real route.
 */
export default function MobileBottomNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const allLeaves = useMemo(() => {
    const perms = user?.permissions || {};
    const tree = buildStaffItems(perms);
    const flat = [];
    tree.forEach((it) => {
      if (it.children && Array.isArray(it.children)) {
        it.children.forEach((c) => flat.push(c));
      } else if (it.to) {
        flat.push(it);
      }
    });
    return flat;
  }, [user?.permissions]);

  // Tabs shown directly in the bar: Dashboard pinned first, then 3 more by
  // priority. Everything else goes into the "More" sheet.
  const primaryOrder = [
    "/dashboard",
    "/students",
    "/students/attendance",
    "/students/individual-attendance",
    "/classes",
    "/employees/list",
    "/employees/attendance",
    "/accounting/income",
    "/memos",
    "/incidents",
    "/notifications",
  ];
  const primaryTabs = useMemo(() => {
    const byTo = new Map(allLeaves.map((l) => [l.to, l]));
    const picked = [];
    for (const path of primaryOrder) {
      if (picked.length === 4) break;
      const it = byTo.get(path);
      if (it) {
        picked.push(it);
        byTo.delete(path);
      }
    }
    // Fill remaining slots from whatever's left in original order.
    for (const it of allLeaves) {
      if (picked.length === 4) break;
      if (byTo.has(it.to)) {
        picked.push(it);
        byTo.delete(it.to);
      }
    }
    return picked;
  }, [allLeaves]);

  const primarySet = new Set(primaryTabs.map((t) => t.to));
  const moreItems = allLeaves.filter((l) => !primarySet.has(l.to));

  const isActive = (to) =>
    location.pathname === to ||
    (to !== "/dashboard" && location.pathname.startsWith(to + "/"));

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#2563EB]/10 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_-12px_rgba(37,99,235,0.18)]"
        data-testid="mobile-bottom-nav"
      >
        <ul className="grid grid-cols-5">
          {primaryTabs.map((it) => {
            const Icon = it.icon || LayoutDashboard;
            const active = isActive(it.to);
            return (
              <li key={it.to}>
                <Link
                  to={it.to}
                  data-testid={`bottom-nav-${(it.label || "").toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 transition ${
                    active ? "text-[#2563EB]" : "text-slate-500"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? "scale-110" : ""}`} />
                  <span className={`text-[10px] leading-tight font-heading font-semibold truncate max-w-[60px] ${active ? "" : ""}`}>
                    {it.label}
                  </span>
                </Link>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              data-testid="bottom-nav-more"
              className="w-full flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 text-slate-500"
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] leading-tight font-heading font-semibold">More</span>
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl max-h-[80vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]"
          data-testid="bottom-nav-more-sheet"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="font-heading text-lg">All sections</SheetTitle>
            <SheetDescription>Quick jump to any module you have access to.</SheetDescription>
          </SheetHeader>
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            {moreItems.map((it) => {
              const Icon = it.icon || LayoutDashboard;
              const active = isActive(it.to);
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  onClick={() => setMoreOpen(false)}
                  data-testid={`more-nav-${(it.label || "").toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl p-3 border transition ${
                    active
                      ? "border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-[#2563EB]/40"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[11px] leading-tight font-heading font-semibold text-center">
                    {it.label}
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-5 pt-5 border-t border-slate-200 grid grid-cols-3 gap-2">
            <Link
              to="/change-password"
              onClick={() => setMoreOpen(false)}
              className="flex flex-col items-center gap-1.5 rounded-2xl p-3 border border-slate-200 text-slate-600 hover:border-[#2563EB]/40"
              data-testid="more-nav-change-password-shortcut"
            >
              <KeyRound className="w-5 h-5" />
              <span className="text-[11px] font-heading font-semibold">Password</span>
            </Link>
            <Link
              to="/help"
              onClick={() => setMoreOpen(false)}
              className="flex flex-col items-center gap-1.5 rounded-2xl p-3 border border-slate-200 text-slate-600 hover:border-[#2563EB]/40"
              data-testid="more-nav-help-shortcut"
            >
              <LifeBuoy className="w-5 h-5" />
              <span className="text-[11px] font-heading font-semibold">Help</span>
            </Link>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex flex-col items-center justify-center gap-1.5 rounded-2xl p-3 border border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-auto whitespace-normal"
              data-testid="more-nav-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-[11px] font-heading font-semibold">Logout</span>
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
